import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { X } from "lucide-react";
import {
  appLocaleMeta,
  appLocales,
  applyDocumentLocale,
  detectInitialLocale,
  persistLocale,
  translate,
  type AppLocale,
  type Translate
} from "./i18n";
import { BRAND } from "./lib/brand";
import type { LegalKind } from "./i18n.types";
import { isPublicPrivacyNoticeVersion, type PublicPrivacyNoticeVersion } from "@tinkr/shared/customer-privacy";
import { JP_MARKET_ENABLED } from "./market";
import { ReviewAndPay } from "./screens/ReviewAndPay";
import { Success } from "./screens/Success";
import { Cancelled } from "./screens/Cancelled";
import { Tracking } from "./screens/Tracking";
import { Handoff } from "./screens/Handoff";
import { OrderPrivacy } from "./screens/OrderPrivacy";
import { RecoverAccess } from "./screens/RecoverAccess";
import { flowCopy } from "./screens/flow-copy";
import { recallThemeForOrder, themeFromSearch, type CheckoutTheme } from "./lib/theme-lock";

const Legal = lazy(() => import("./screens/Legal").then((m) => ({ default: m.Legal })));

export type Route =
  | { name: "handoff"; token: string }
  | { name: "privacy"; token: string }
  | { name: "recovery"; token: string }
  | { name: "review"; token: string }
  | { name: "tracking"; token: string }
  | { name: "paid"; orderId: string }
  | { name: "cancelled"; orderId: string }
  | { name: "legal"; kind: LegalKind; noticeVersion?: PublicPrivacyNoticeVersion }
  | { name: "notfound" };

export type { LegalKind };

export function parseRoute(pathname: string): Route {
  const handoff = /^\/start\/([A-Za-z0-9_-]{16,128})$/.exec(pathname);
  if (handoff) return { name: "handoff", token: handoff[1]! };
  const privacy = /^\/o\/([A-Za-z0-9_-]{16,128})\/privacy$/.exec(pathname);
  if (privacy) return { name: "privacy", token: privacy[1]! };
  const recovery = /^\/o\/([A-Za-z0-9_-]{16,128})\/recover$/.exec(pathname);
  if (recovery) return { name: "recovery", token: recovery[1]! };
  const tracking = /^\/o\/([A-Za-z0-9_-]{16,128})\/status$/.exec(pathname);
  if (tracking) return { name: "tracking", token: tracking[1]! };
  const review = /^\/o\/([A-Za-z0-9_-]{16,128})$/.exec(pathname);
  if (review) return { name: "review", token: review[1]! };
  const paid = /^\/orders\/([^/]+)\/paid$/.exec(pathname);
  if (paid) return { name: "paid", orderId: paid[1]! };
  const cancelled = /^\/orders\/([^/]+)\/cancelled$/.exec(pathname);
  if (cancelled) return { name: "cancelled", orderId: cancelled[1]! };
  if (pathname === "/legal/terms") return { name: "legal", kind: "terms" };
  if (pathname === "/legal/privacy") return { name: "legal", kind: "privacy" };
  // Every published notice stays at its own URL (consent evidence for older handoffs).
  const privacyVersion = pathname.match(/^\/legal\/privacy\/(\d+\.\d+)$/)?.[1];
  if (isPublicPrivacyNoticeVersion(privacyVersion)) {
    return { name: "legal", kind: "privacy", noticeVersion: privacyVersion };
  }
  if (pathname === "/legal/refund") return { name: "legal", kind: "refund" };
  if (pathname === "/legal/seller") return { name: "legal", kind: "mor" };
  if (pathname === "/legal/tokushoho") {
    // Japan launch gate: the 特商法 disclosure still carries placeholder fields
    // and must be unreachable until the JP market opens. The seller disclosure
    // is the closest equivalent content for anyone holding an old link.
    return { name: "legal", kind: JP_MARKET_ENABLED ? "tokushoho" : "mor" };
  }
  return { name: "notfound" };
}

// The theme is merchant-driven, never device-driven: a `?theme=` lock wins, then
// the per-order stash (the Stripe success/cancel URLs carry no query, so the
// post-payment screens recall the lock the review screen stashed), then light.
// There is deliberately no toggle, no localStorage preference, and no
// prefers-color-scheme fallback — a transient pay page should match the app the
// customer came from, and both merchant apps (Doodles: always light, MakerChip:
// passes its active theme) say so explicitly.
function resolveTheme(route: Route): CheckoutTheme {
  const locked = themeFromSearch(window.location.search);
  if (locked) return locked;
  if (route.name === "paid" || route.name === "cancelled") {
    const recalled = recallThemeForOrder(route.orderId);
    if (recalled) return recalled;
  }
  return "light";
}

export function App({ initialLocale }: { initialLocale: AppLocale }) {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));
  const [theme] = useState<CheckoutTheme>(() => resolveTheme(parseRoute(window.location.pathname)));
  const [locale, setLocale] = useState<AppLocale>(initialLocale ?? detectInitialLocale());

  const t: Translate = useMemo(() => (key) => translate(locale, key), [locale]);

  const navigate = useCallback((path: string): void => {
    window.history.pushState({}, "", path + window.location.search);
    setRoute(parseRoute(path));
    window.scrollTo({ top: 0 });
  }, []);

  const enterPrivateCheckout = useCallback((path: string): void => {
    // Remove the partner-visible bootstrap or expired credential from history.
    window.history.replaceState({}, "", `${path}?lang=${locale}`);
    setRoute(parseRoute(path));
    window.scrollTo({ top: 0 });
  }, [locale]);

  useEffect(() => {
    const onPop = (): void => setRoute(parseRoute(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Keep the browser chrome tint (mobile address bar) on the resolved theme;
  // the static index.html default is the light topbar surface.
  useEffect(() => {
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", theme === "dark" ? "#0d1715" : "#ffffff");
  }, [theme]);

  const pageTitle = route.name === "privacy" ? flowCopy(locale).privacyTitle
    : route.name === "handoff" ? t("review.delivery.title")
      : route.name === "recovery" ? flowCopy(locale).recoverTitle
        : t(route.name === "paid" ? "chrome.paidTitle"
          : route.name === "cancelled" ? "chrome.paymentTitle"
            : route.name === "tracking" ? "success.track" : "chrome.reviewTitle");

  // The browser tab follows the active locale and screen (index.html only
  // carries the brand, so no hardcoded language is ever shown before this runs).
  useEffect(() => {
    document.title = `${pageTitle} | Tinkr`;
  }, [pageTitle]);

  useEffect(() => {
    applyDocumentLocale(locale);
    persistLocale(locale);
    const url = new URL(window.location.href);
    if (url.searchParams.get("lang") !== locale) {
      url.searchParams.set("lang", locale);
      window.history.replaceState({}, "", url);
    }
  }, [locale]);

  return (
    <div className="co-shell" data-theme={theme}>
      <header className="co-topbar">
        <div className="co-topbar-left">
          <button
            type="button"
            className="co-icon-btn"
            aria-label={t("chrome.close")}
            onClick={() => {
              if (window.history.length > 1) window.history.back();
              else window.location.assign(BRAND.marketingUrl);
            }}
          >
            <X size={18} aria-hidden />
          </button>
          {/* Route-aware title (design C5/C6): the success and cancelled
              screens are not "Review & Pay". */}
          <span className="co-topbar-title">
            {pageTitle}
          </span>
        </div>
        <div className="co-topbar-right">
          <label className="co-lang">
            <span className="co-sr-only">{t("chrome.language")}</span>
            <select
              className="co-lang-select"
              value={locale}
              aria-label={t("chrome.language")}
              onChange={(event) => setLocale(event.target.value as AppLocale)}
            >
              {appLocales.map((code) => (
                <option key={code} value={code}>
                  {appLocaleMeta[code].label}
                </option>
              ))}
            </select>
          </label>
          <a href={BRAND.marketingUrl} target="_blank" rel="noopener noreferrer">
            <img className="co-logo" src={BRAND.logos.fullColorHorizontal} alt={t("chrome.logoAlt")} />
          </a>
        </div>
      </header>

      <ErrorBoundary
        resetKeys={[route]}
        fallbackRender={({ resetErrorBoundary }) => (
          <div className="co-state">
            <p>{t("common.error")}</p>
            <button type="button" className="co-btn-secondary" onClick={resetErrorBoundary}>
              {t("common.retry")}
            </button>
          </div>
        )}
      >
        <Suspense fallback={<div className="co-state"><div className="co-spinner" aria-hidden /></div>}>
          {route.name === "handoff" && <Handoff key={route.token} token={route.token} locale={locale} onVerified={enterPrivateCheckout} />}
          {route.name === "privacy" && <OrderPrivacy key={route.token} token={route.token} locale={locale} />}
          {route.name === "recovery" && <RecoverAccess key={route.token} token={route.token} locale={locale} onVerified={enterPrivateCheckout} />}
          {route.name === "review" && <ReviewAndPay key={route.token} t={t} locale={locale} token={route.token} navigate={navigate} />}
          {route.name === "tracking" && <Tracking key={route.token} token={route.token} locale={locale} />}
          {route.name === "paid" && <Success key={route.orderId} t={t} locale={locale} orderId={route.orderId} navigate={navigate} />}
          {route.name === "cancelled" && <Cancelled key={route.orderId} t={t} orderId={route.orderId} navigate={navigate} />}
          {route.name === "legal" && <Legal t={t} locale={locale} kind={route.kind}
            noticeVersion={route.noticeVersion} navigate={navigate} />}
          {route.name === "notfound" && (
            <div className="co-state">
              <p>{t("error.notFound")}</p>
            </div>
          )}
        </Suspense>
      </ErrorBoundary>

      <footer className="co-footer">
        <div className="co-footer-links">
          <button type="button" onClick={() => navigate("/legal/terms")}>{t("footer.terms")}</button>
          <button type="button" onClick={() => navigate("/legal/privacy")}>{t("footer.privacy")}</button>
          <button type="button" onClick={() => navigate("/legal/refund")}>{t("footer.refund")}</button>
          <button type="button" onClick={() => navigate("/legal/seller")}>{t("footer.seller")}</button>
          {JP_MARKET_ENABLED && (
            <button type="button" onClick={() => navigate("/legal/tokushoho")}>{t("footer.tokushoho")}</button>
          )}
        </div>
        <p>© {new Date().getFullYear()} {t("footer.entity")} · {t("footer.companyNo")} {BRAND.companyNumber}</p>
      </footer>
    </div>
  );
}
